import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot, doc, setDoc, addDoc, updateDoc, increment, getDoc, getDocs, writeBatch, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { getCurrentUserId } from '../../utils/auth';
import './index.css';

// Replicate Dart's String.hashCode to ensure we generate the exact same chatRoomId as the mobile app
const dartHashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash + str.charCodeAt(i)) & 0x1FFFFFFF;
        hash = (hash + ((hash & 0x0007FFFF) << 10)) & 0x1FFFFFFF;
        hash ^= hash >>> 6;
    }
    hash = (hash + ((hash & 0x03FFFFFF) << 3)) & 0x1FFFFFFF;
    hash ^= hash >>> 11;
    hash = (hash + ((hash & 0x00003FFF) << 15)) & 0x1FFFFFFF;
    return hash;
};

const getChatRoomId = (currentUserId, peerId) => {
    if (dartHashCode(currentUserId) <= dartHashCode(peerId)) {
        return `${currentUserId}_${peerId}`;
    } else {
        return `${peerId}_${currentUserId}`;
    }
};

const MessagesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const [currentUserId, setCurrentUserId] = useState(null);
    const [currentUserName, setCurrentUserName] = useState('');
    const [currentUserImage, setCurrentUserImage] = useState('');
    
    const [recentChats, setRecentChats] = useState([]);
    const [activeChat, setActiveChat] = useState(null); // The peer we are talking to
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    
    const messagesEndRef = useRef(null);

    // If navigated from Users page with a pre-selected user
    useEffect(() => {
        if (location.state && location.state.peerId) {
            setActiveChat({
                userId: location.state.peerId,
                userName: location.state.peerName,
                userProfileImage: location.state.peerImage,
                subCatId: location.state.subCatId || ''
            });
            window.history.replaceState({}, document.title)
        }
    }, [location.state]);

    // Lock body scroll to make it feel like WhatsApp/Instagram web
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    useEffect(() => {
        const id = getCurrentUserId();
        if (!id) {
            navigate('/login');
            return;
        }
        setCurrentUserId(id);
        let storedName = localStorage.getItem('name_key');
        if (!storedName) {
            try {
                const userDataStr = localStorage.getItem('userData');
                if (userDataStr) {
                    const userData = JSON.parse(userDataStr);
                    storedName = userData.userName || userData.fullName;
                }
            } catch(e) {}
        }
        setCurrentUserName(storedName || 'Unknown User');
        const profile = localStorage.getItem('userProfileImage');
        setCurrentUserImage(profile ? `https://www.whysocial.in/clap-kartel/public/${profile}` : '');
    }, [navigate]);

    // Load Recent Chats
    useEffect(() => {
        if (!currentUserId) return;

        const recentChatsRef = collection(db, 'recent_chats', currentUserId, 'users');
        const q = query(recentChatsRef, orderBy('timestamp', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const chats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRecentChats(chats);
        });

        return () => unsubscribe();
    }, [currentUserId]);

    // Load Messages for Active Chat
    useEffect(() => {
        if (!currentUserId || !activeChat) return;

        const chatRoomId = getChatRoomId(currentUserId, activeChat.userId);
        
        const messagesRef = collection(db, 'messages', chatRoomId, 'chats');
        const q = query(messagesRef, orderBy('timestamp', 'asc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMessages(msgs);
            
            // Mark seen
            markMessagesSeen(chatRoomId);
        });

        return () => unsubscribe();
    }, [currentUserId, activeChat]);

    // Scroll to bottom on new message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const markMessagesSeen = async (chatRoomId) => {
        if (!activeChat || !currentUserId) return;
        try {
            const messagesRef = collection(db, 'messages', chatRoomId, 'chats');
            const unreadQuery = query(messagesRef, where('receiverId', '==', currentUserId), where('seen', '==', false));
            const snapshot = await getDocs(unreadQuery);

            if (!snapshot.empty) {
                const batch = writeBatch(db);
                snapshot.docs.forEach((docSnap) => {
                    batch.update(docSnap.ref, { seen: true });
                });
                await batch.commit();
            }

            // Reset recent chats unread count
            const recentRef = doc(db, 'recent_chats', currentUserId, 'users', activeChat.userId);
            await setDoc(recentRef, { unreadCount: 0 }, { merge: true });
        } catch (error) {
            console.error("Error marking seen:", error);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        const text = newMessage.trim();
        if (!text || !activeChat) return;

        setNewMessage('');
        const chatRoomId = getChatRoomId(currentUserId, activeChat.userId);
        const timestamp = Date.now();

        try {
            // Add message
            const messagesRef = collection(db, 'messages', chatRoomId, 'chats');
            await addDoc(messagesRef, {
                senderId: currentUserId,
                receiverId: activeChat.userId,
                message: text,
                timestamp: timestamp,
                seen: false,
                type: 'text',
                subCatId: activeChat.subCatId || ''
            });

            // Update Recent Chats (Sender)
            const senderRef = doc(db, 'recent_chats', currentUserId, 'users', activeChat.userId);
            await setDoc(senderRef, {
                userId: activeChat.userId,
                userName: activeChat.userName,
                userProfileImage: activeChat.userProfileImage,
                lastMessage: text,
                timestamp: timestamp,
                unreadCount: 0,
                subCatId: activeChat.subCatId || ''
            }, { merge: true });

            // Update Recent Chats (Receiver)
            const receiverRef = doc(db, 'recent_chats', activeChat.userId, 'users', currentUserId);
            await setDoc(receiverRef, {
                userId: currentUserId,
                userName: currentUserName,
                userProfileImage: currentUserImage,
                lastMessage: text,
                timestamp: timestamp,
                unreadCount: increment(1),
                subCatId: activeChat.subCatId || '' // using peer's subCatId as fallback
            }, { merge: true });

        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    const parseTimestamp = (timestamp) => {
        if (!timestamp) return null;
        if (typeof timestamp.toDate === 'function') {
            return timestamp.toDate();
        }
        if (timestamp.seconds) {
            return new Date(timestamp.seconds * 1000);
        }
        const parsed = new Date(timestamp);
        return isNaN(parsed.getTime()) ? null : parsed;
    };

    const formatTime = (ts) => {
        const date = parseTimestamp(ts);
        if (!date) return '';
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (!currentUserId) return null;

    return (
        <div className="messages-layout">
            <div className={`messages-sidebar ${activeChat ? 'hide-on-mobile' : ''}`}>
                <div className="sidebar-header">
                    <h2>Messages</h2>
                </div>
                <div className="recent-chats-list">
                    {recentChats.length === 0 ? (
                        <p className="no-chats-message">No recent chats</p>
                    ) : (
                        recentChats.map((chat) => (
                            <div 
                                key={chat.userId} 
                                className={`chat-list-item ${activeChat?.userId === chat.userId ? 'active' : ''}`}
                                onClick={() => setActiveChat(chat)}
                            >
                                <img 
                                    src={chat.userProfileImage || 'https://placehold.co/120?text=No+Image'} 
                                    alt={chat.userName} 
                                    className="chat-avatar"
                                    onError={(e) => { e.target.src = 'https://placehold.co/120?text=No+Image'; }}
                                />
                                <div className="chat-list-info">
                                    <div className="chat-list-header">
                                        <h4>{chat.userName}</h4>
                                        <span className="chat-time">{formatTime(chat.timestamp)}</span>
                                    </div>
                                    <div className="chat-list-preview">
                                        <p>{chat.lastMessage}</p>
                                        {chat.unreadCount > 0 && (
                                            <span className="unread-badge">{chat.unreadCount}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className={`messages-main ${!activeChat ? 'hide-on-mobile' : ''}`}>
                {activeChat ? (
                    <>
                        <div className="chat-header">
                            <button className="back-btn mobile-only" onClick={() => setActiveChat(null)}>
                                ← Back
                            </button>
                            <img 
                                src={activeChat.userProfileImage || 'https://placehold.co/120?text=No+Image'} 
                                alt={activeChat.userName} 
                                className="chat-header-avatar"
                                onError={(e) => { e.target.src = 'https://placehold.co/120?text=No+Image'; }}
                            />
                            <h3>{activeChat.userName}</h3>
                        </div>

                        <div className="chat-messages">
                            {messages.map((msg) => {
                                const isMe = msg.senderId === currentUserId;
                                return (
                                    <div key={msg.id} className={`message-bubble-wrapper ${isMe ? 'mine' : 'theirs'}`}>
                                        <div className="message-bubble">
                                            <p>{msg.message}</p>
                                            <span className="message-time">{formatTime(msg.timestamp)}</span>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        <form className="chat-input-area" onSubmit={handleSendMessage}>
                            <input 
                                type="text" 
                                placeholder="Type a message..." 
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                            />
                            <button type="submit" disabled={!newMessage.trim()} title="Send Message">
                                <svg className="send-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                </svg>
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="no-active-chat">
                        <div className="no-chat-icon">💬</div>
                        <h3>Select a chat to start messaging</h3>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessagesPage;
